import { Box, Chip, ListItem, ListItemIcon, ListItemText } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { FeatureName } from "@/common/constants/featureName";
import { RepairHelper } from "@/common/helpers/entity/repair";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  RepairMaterialDto,
  RepairMaterialGetPaginatedDto,
} from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import AppTypography from "../../Text/AppTypography";
import RepairMaterialCreateUpdateModal from "./RepairMaterialCreateUpdateModal";
import RepairMaterialCrossTenantSelectorPopover from "./RepairMaterialCrossTenantSelectorPopover";
import RepairMaterialViewModal from "./View/RepairMaterialViewModal";

const defaultDisplayProps = {
  viewInModal: true,
  editInModal: true,
  crossTenantSelector: true,
};

export type RepairMaterialAutocompleteOption = BaseAutocompleteOption<RepairMaterialDto>;

export const getRepairMaterialOptionTitle = (data: RepairMaterialDto): string =>
  StringHelper.join([data.name || data.localNumber], ", ", {
    skipEmpty: true,
  });

export const repairMaterialToOption = (
  data: RepairMaterialDto,
): RepairMaterialAutocompleteOption => ({
  id: data.id!,
  title: getRepairMaterialOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface RepairMaterialAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<RepairMaterialDto> {
  searchFilters?: Partial<Omit<RepairMaterialGetPaginatedDto, "search" | "includeIds">>;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export default function RepairMaterialAutocomplete({
  searchFilters,
  displayProps,

  isPreload = true,
  ...otherProps
}: RepairMaterialAutocompleteProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();
  const authorizationInfo = useAuthorizationInfo();

  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={repairMaterialToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.repairMaterialsApi.apiV1RepairMaterialsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          repairMaterialGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          repairMaterialGetPaginatedDto: {
            ...params.repairMaterialGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Repair material'
      placeholder='Search for repair material...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='repairMaterial' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <AppTypography
                    ellipsing={{ enabled: true }}
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </AppTypography>
                </>
              }
              secondary={
                option.data && (
                  <Box>
                    <AppTypography ellipsing={{ enabled: true }} variant='body2'>
                      {option.data?.description}
                    </AppTypography>

                    {RepairHelper.isExternal({
                      tenantId: currentTenant?.id,
                      repairEntity: option.data,
                    }) && (
                      <Box>
                        <Chip
                          variant='outlined'
                          size='extraSmall'
                          label={
                            <>
                              <AppIcon of='external' inText /> External
                            </>
                          }
                        />
                      </Box>
                    )}
                  </Box>
                )
              }
            />
          </ListItem>
        );
      }}
      entityViewModal={{
        if:
          displayProps?.viewInModal &&
          authorizationInfo.hasPermissions([AppPermission.RepairCatalogRead]),
        renderModal: (params) => (
          <RepairMaterialViewModal
            {...params.dialogProps}
            viewProps={{
              repairMaterial: params.entity,
            }}
          />
        ),
      }}
      entityEditModal={{
        if:
          displayProps?.editInModal &&
          authorizationInfo.hasPermissions([AppPermission.RepairCatalogManage]),
        renderModal: (params) => (
          <RepairMaterialCreateUpdateModal
            {...params.dialogProps}
            createUpdateProps={{
              repairMaterialId: params.entity.id,
              repairMaterial: params.entity,
              onSave: (newValue) => {
                params.handleEntityUpdated(newValue);
                params.dialogProps?.onClose?.({}, "escapeKeyDown");
              },
            }}
          />
        ),
      }}
      endAdornmentActions={{
        actions: [
          {
            if:
              displayProps?.crossTenantSelector &&
              featureManager.isEnabled(FeatureName.CrossTenantRepairEntityAccess),
            renderAction: (params) => (
              <RepairMaterialCrossTenantSelectorPopover
                clickBehavior={{}}
                trigger={
                  <AppIconButton
                    {...params.iconButtonProps}
                    title='My cross-company repair materials'
                  >
                    <AppIcon of='crossTenantBrowse' />
                  </AppIconButton>
                }
                selectorProps={{
                  onSave: (newValue) => {
                    console.log("Cross-tenant repair material selected", newValue);
                    if (newValue?.repairEntity) {
                      params.handleEntitySelected(newValue?.repairEntity);
                    }
                  },
                  onCancel: () => {},
                }}
              />
            ),
          },
        ],
      }}
      {...otherProps}
    />
  );
}
